import { useMemo } from 'react';
import {
  useVisitsInRange,
  type CustomRangeBounds,
  type RangeKey,
  type VisitRow,
} from '@busy/api';

// Operational averages computed from visits in the dashboard's range.
// Powers the "Averages" collapsible tile — the small per-day / per-cut
// / per-client numbers a barber needs to spot patterns in their work.
//
// Computed client-side from the same visits payload the Cut Log uses,
// so this hook adds zero new network roundtrips beyond what's already
// fetched. (useVisitsInRange caches per-range, so the metrics tile and
// the averages tile share the underlying request.)
//
// What's included (matches the product spec — 8 metrics + 3 footnotes):
//
//   Per day                | Per cut/client
//   ───────────────────────|─────────────────────
//   cuts_per_day           | revenue_per_cut
//   hours_per_day          | minutes_per_cut
//   income_per_day         | tip_per_cut
//                          | revenue_per_client
//                          | tip_rate_pct
//
//   Footnotes:
//     - walkin_pct (under cuts_per_day) — % of cuts tagged as walk-in
//     - addon_attach_pct (under revenue_per_cut) — % of cuts with ≥1 add-on
//     - tip_attach_pct (under tip_per_cut) — % of cuts that received a tip
//
// All values are 0 (or null where the denominator is 0) when the range
// is empty. The tile surfaces a clear "—" / disclaimer in that case
// rather than rendering misleading zeros.

export type OperationalAverages = {
  // Totals (also exposed so the tile can render the disclaimer footer)
  total_cuts: number;
  days_worked: number;

  // Per day
  cuts_per_day: number;
  hours_per_day: number;
  income_per_day_cents: number;

  // Per cut
  revenue_per_cut_cents: number;
  minutes_per_cut: number;
  tip_per_cut_cents: number;

  // Per client
  revenue_per_client_cents: number | null;
  tip_rate_pct: number | null;

  // Footnote attach rates — null when no cuts in range (no denominator)
  walkin_pct: number | null;        // null when no cuts tagged as walkin/appt
  walkin_appointment_tagged: number; // count of cuts with was_appointment != null
  addon_attach_pct: number | null;
  tip_attach_pct: number | null;
};

const ZERO_AVERAGES: OperationalAverages = {
  total_cuts: 0,
  days_worked: 0,
  cuts_per_day: 0,
  hours_per_day: 0,
  income_per_day_cents: 0,
  revenue_per_cut_cents: 0,
  minutes_per_cut: 0,
  tip_per_cut_cents: 0,
  revenue_per_client_cents: null,
  tip_rate_pct: null,
  walkin_pct: null,
  walkin_appointment_tagged: 0,
  addon_attach_pct: null,
  tip_attach_pct: null,
};

/** Pure helper — visits → averages. Exported for direct testing /
 *  reuse (e.g. recap computation). */
export function computeOperationalAverages(visits: VisitRow[]): OperationalAverages {
  if (visits.length === 0) return ZERO_AVERAGES;

  let totalRevenue = 0;
  let totalTips = 0;
  let totalMinutes = 0;
  let addonVisits = 0;
  let tipVisits = 0;
  let walkinCount = 0;
  let appointmentCount = 0;
  const distinctClients = new Set<string>();
  const workedDays = new Set<string>();

  for (const v of visits) {
    totalRevenue += v.price_cents ?? 0;
    totalTips += v.tip_cents ?? 0;
    totalMinutes += v.duration_min ?? 0;
    if (Array.isArray(v.addons) && v.addons.length > 0) addonVisits += 1;
    if ((v.tip_cents ?? 0) > 0) tipVisits += 1;
    if (v.was_appointment === true) appointmentCount += 1;
    else if (v.was_appointment === false) walkinCount += 1;
    if (v.client_id) distinctClients.add(v.client_id);
    // Local-day key — use the start of the visit's date in local time
    // so two visits at 9pm and the next 1am count as different days
    // when they cross midnight (rare but correct).
    const d = new Date(v.started_at);
    const dayKey = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    workedDays.add(dayKey);
  }

  const totalCuts = visits.length;
  const daysWorked = workedDays.size;
  const totalIncome = totalRevenue + totalTips;
  const tagged = walkinCount + appointmentCount;

  return {
    total_cuts: totalCuts,
    days_worked: daysWorked,

    cuts_per_day: daysWorked > 0 ? totalCuts / daysWorked : 0,
    hours_per_day: daysWorked > 0 ? totalMinutes / 60 / daysWorked : 0,
    income_per_day_cents:
      daysWorked > 0 ? Math.round(totalIncome / daysWorked) : 0,

    revenue_per_cut_cents:
      totalCuts > 0 ? Math.round(totalRevenue / totalCuts) : 0,
    minutes_per_cut:
      totalCuts > 0 ? totalMinutes / totalCuts : 0,
    tip_per_cut_cents:
      totalCuts > 0 ? Math.round(totalTips / totalCuts) : 0,

    revenue_per_client_cents:
      distinctClients.size > 0
        ? Math.round(totalRevenue / distinctClients.size)
        : null,
    tip_rate_pct:
      totalRevenue > 0 ? (totalTips / totalRevenue) * 100 : null,

    walkin_pct:
      tagged > 0 ? Math.round((walkinCount / tagged) * 100) : null,
    walkin_appointment_tagged: tagged,
    addon_attach_pct:
      totalCuts > 0 ? Math.round((addonVisits / totalCuts) * 100) : null,
    tip_attach_pct:
      totalCuts > 0 ? Math.round((tipVisits / totalCuts) * 100) : null,
  };
}

/** Hook wrapper — fetches visits in the dashboard's range and returns
 *  the computed averages. Range-aware: changes with the dashboard's
 *  range selector. */
export function useOperationalAverages(
  range: RangeKey,
  customBounds: CustomRangeBounds | null,
): {
  data: OperationalAverages | null;
  loading: boolean;
} {
  const visits = useVisitsInRange(range, customBounds);
  const data = useMemo(() => {
    if (visits.loading) return null;
    return computeOperationalAverages(visits.data ?? []);
  }, [visits.loading, visits.data]);
  return { data, loading: visits.loading };
}
